import { ChangeEvent, memo, useCallback, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import styled from 'styled-components'
import { addQuestionMessage, messagesSelectors, toggleBotTyping, toggleUserTyping } from 'store/messages'
import { createUserMessage } from 'utils/helpers'
import { useTranslation } from 'react-i18next'
import clsx from 'clsx'
import { useGetBotMode } from 'hooks/useGetBotMode'
import { Icon } from 'components/ui/Icon'
import { askBotAnswer } from 'api/index'
import { sentMessageToOperator } from 'api/chats'
import { WidgetFileUpload } from 'components/WidgetFileUpload'
import { ModalUploadManager } from '../managers/modal/modal-upload-manager'
import { useGetSetting } from 'api/settings'
import { Spinner } from 'components/ui/Spinner'

const Textarea = styled.textarea`
  -ms-overflow-style: none;
  scrollbar-width: none;
  &::-webkit-scrollbar {
    display: none;
  }
`

export const WidgetKeypadComponent = () => {
  const dispatch = useDispatch()
  const { isAppMode } = useGetBotMode()
  const { t } = useTranslation()
  const { setting } = useGetSetting()
  const [files, setFiles] = useState<File[]>([])
  const [loading, setLoading] = useState(false)
  const [userInput, setUserInput] = useState('')
  const userTypingPlaceholder = useSelector(messagesSelectors.userTypingPlaceholder)
  const userTyping = useSelector(messagesSelectors.userTyping)
  const isInOperatorMode = useSelector(messagesSelectors.currentChatIsInOperatorMode)
  const disableFields = useSelector(messagesSelectors.disableFieldsTicketClosed)

  const handleSubmit = useCallback(async () => {
    try {
      if (userInput?.length > 0) {
        if (isInOperatorMode) {
          ModalUploadManager.hide()
          setLoading(true)
          await sentMessageToOperator({
            message: userInput.trim(),
            files,
          })
          clearFiles()
          setLoading(false)
          clearFiles()
          setUserInput('')
        } else {
          const question = userInput.trim()
          const userMessage = createUserMessage(question)
          const { message, _id: questionId, senderId, chatSenderRole } = userMessage
          dispatch(
            addQuestionMessage({
              message,
              _id: questionId,
              senderId,
              chatSenderRole,
            }),
          )
          setUserInput('')
          dispatch(toggleUserTyping(false))
          dispatch(toggleBotTyping(true))
          await askBotAnswer({ question, questionId })
        }
      }
    } catch (e) {
      console.error({ e })
    }
  }, [dispatch, userInput, isInOperatorMode])

  const onChange = useCallback((e: ChangeEvent<HTMLTextAreaElement>) => {
    setUserInput(e.target.value)
  }, [])

  const onFilesAdded = (files: File[]) => {
    setFiles((prevFiles) => [...prevFiles, ...files])
  }

  const onFileRemoved = (index: number) => {
    setFiles((prevFiles) => prevFiles.filter((_, i) => i !== index))
  }

  const clearFiles = () => {
    setFiles([])
  }

  return (
    <div
      className={clsx(
        'tw-mt-auto tw-flex tw-items-center tw-border-t-white tw-bg-primary-500 tw-border-t-2 tw-border-white',
        isAppMode ? `tw-h-[4.8rem]` : `tw-h-[4rem]`,
        isAppMode ? '' : 'tw-rounded-t-3xl tw-rounded-b-6xl',
        disableFields ? 'tw-opacity-50 tw-blur-[1px]' : '',
      )}
    >
      {isInOperatorMode && (
        <WidgetFileUpload
          disable={disableFields}
          clearFiles={clearFiles}
          files={files}
          onFilesAdded={onFilesAdded}
          onFilesRemoved={onFileRemoved}
        />
      )}
      <Textarea
        rows={1}
        disabled={disableFields}
        className={`tw-mr-2 tw-block tw-w-full tw-resize-none tw-bg-primary-500 tw-p-2.5 tw-text-sm tw-text-white tw-outline-none tw-placeholder-white
          ${userTyping ? 'tw-cursor-default' : 'tw-cursor-not-allowed'}
          ${isInOperatorMode ? '' : 'tw-ml-2'}
        `}
        placeholder={t(userTypingPlaceholder)}
        value={userInput}
        onChange={onChange}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault()
            handleSubmit().catch(console.error)
          }
        }}
        readOnly={!userTyping}
      />
      <button
        type="submit"
        disabled={!userInput || disableFields}
        className={`${
          userInput.trim().length > 1 ? 'tw-cursor-default' : 'tw-cursor-not-allowed'
        } tw-hover:bg-primary-500 tw-inline-flex tw-justify-center tw-rounded-full tw-p-2 `}
        style={{ color: setting?.primaryColor || 'black' }}
        onClick={(e) => {
          e.preventDefault()
          handleSubmit().catch(console.error)
        }}
      >
        {loading ? (
          <Spinner color={setting?.secondaryColor} />
        ) : (
          <Icon icon={['far', 'paper-plane-top']} className="tw-h-6 tw-w-6 tw-stroke-[1.1px] tw-text-white" />
        )}
      </button>
    </div>
  )
}

export const WidgetKeypad = memo(WidgetKeypadComponent)
WidgetKeypad.displayName = 'WidgetKeypad'
